import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import {
    AcceptLogisticRequest,
    AddEmployeesVehiclesLogisticRequest,
    RejectLogisticRequest
} from "../../../../reduxToolkit/Logistics/logisticRequestSectionApi";
import { toast } from "react-toastify";
import CustomModal from "../../../../components/Modal/CustomModal";
import { CustomTextField } from "../../../../components/CustomTextField";
import { Box } from "@mui/material";
import { CustomExpandableTableStyles } from "../../../../components/Styles";
import CustomAddChipSearch from "../../../../components/CustomAddChipSearch";
import {
    errorMsg,
    userListToLabelEmailPhoneValue,
    vehicleDataTransform
} from "../../../../Apis/functions";
import { PersonChipBlack } from "../../../../components/buttons/IconButtons";
import { TABLES } from "../../../../Apis/Tables";
import {SEARCH_MODEL, SEARCH_USERS_MODEL} from "../../../../components/table/Model/TableModel";
import { SERVICE_ADVANCED_SEARCH_SIMPLE_SEARCH_USER } from "../../../../reduxToolkit/Commons/CommonsApi";

/**
 * @file RequestCommentModal.jsx
 * @email azapata@ibl.mx
 * @author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @description
 * This component renders a modal used for handling logistics request comments.
 * Depending on the props, it allows the user to:
 * - Accept a logistics request
 * - Reject it with a comment
 * - Assign it by selecting an employee and a vehicle
 * It integrates with Redux for dispatching actions and supports internationalization with i18next.
 */

/**
 * Renders a modal dialog to either accept, reject, or assign a logistics request.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.row - The logistics request data
 * @param {Function} props.onHide - Function to close the modal
 * @param {string} props.title - Title of the modal
 * @param {Function} props.setModified - State setter to indicate modifications
 * @param {boolean} props.modified - Indicates if modifications have been made
 * @param {number|string} props.supplierId - ID of the supplier
 * @param {Function} [props.renderExpandedRow] - Optional function to render extra row details
 * @param {boolean} [props.isDecline=false] - Indicates if this modal is for rejecting a request
 * @param {boolean} [props.assign=false] - Indicates if this modal is for assigning a request
 * @param {boolean} [props.details=false] - Indicates if modal is in read-only detail mode
 * @returns {JSX.Element}
 */

const RequestCommentModal  = (props) => {
    const {
        row,
        onHide,
        title,
        setModified,
        supplierId,
        renderExpandedRow,
        isDecline,
        assign,
        details
    } = props;

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [comment, setComment] = useState('');
    const [employee, setEmployee] = useState('');
    const [vehicle, setVehicle] = useState('');
    const [emptyFields, setEmptyFields] = useState(false);
    const [selectedListEmployee, setSelectedListEmployee] = useState([]);
    const [selectedListVehicle, setSelectedListVehicle] = useState([]);

    /**
     * Handles comment input change.
     * @param {Event} e - Input event
     */
    const handleChangeComment = (e) => {
        setComment(e.target.value);
    };
    /**
     * Handles employee selection change.
     * @param {Event} e - Selection event
     */
    useEffect(() => {
        if (selectedListEmployee?.length > 0) {
            setEmployee(selectedListEmployee[0]?.id);
        } else {
            setEmployee('');
        }
    }, [selectedListEmployee]);

    /**
     * Handles vehicle selection change.
     * @param {Event} e - Selection event
     */
    useEffect(() => {
        if (selectedListVehicle?.length > 0) {
            setVehicle(selectedListVehicle[0]?.id);
        } else {
            setVehicle('');
        }
    }, [selectedListVehicle]);

    /**
     * Validates the fields based on modal mode.
     * @returns {boolean}
     */
    const validateFields = () => {
        if (isDecline) return comment.trim();
        if (assign) return employee.trim() && vehicle.trim();
        return true;
    };
    /**
     * Resets all modal inputs to default values.
     */
    const resetValues = () => {
        setComment('');
        setEmployee('');
        setVehicle('');
    };
    /**
     * Builds the payload for the API request.
     * @returns {Object}
     */
    const buildPayload = () => {
        const base = {
            logisticRequestId: row.id,
            supplierId
        };
        if (isDecline) return { ...base, comment };
        if (assign) return { ...base, userId: employee, vehicleId: vehicle };
        return base;
    };
    /**
     * Resolves the API action to be dispatched.
     * @returns {Function}
     */
    const resolveAction = () => {
        if (isDecline) return RejectLogisticRequest;
        if (assign) return AddEmployeesVehiclesLogisticRequest;
        return AcceptLogisticRequest;
    };
    /**
     * Handles form submission: validates input, builds payload, and dispatches action.
     */
    const handleSubmit = () => {
        if (!validateFields()) {
            setEmptyFields(true);
            return;
        }

        const payload = buildPayload();
        const action = resolveAction();

        dispatch(action(payload)).then(({ payload: { data: { success,code } } }) => {
            if (success) {
                toast.success(
                    t(isDecline ? 'has_been_rejected_successfully' : assign
                        ? 'has_been_assigned_successfully'
                        : 'has_been_accepted_successfully'
                    ));
                setModified(true);
                onHide();
            } else errorMsg(code)
        });
    };
    // Reset values when modal is closed
    useEffect(() => {
        resetValues();
        setEmptyFields(false);
    }, [onHide]);

    const bodyAutocompleteSearchEmployee = SEARCH_MODEL.setObject({
        ...SEARCH_USERS_MODEL.pagination,
        model: TABLES.SUPPLIER_EMPLOYEE,
        userId: supplierId
    });

    const bodyAutocompleteSearchVehicle = SEARCH_MODEL.setObject({
        ...SEARCH_USERS_MODEL.pagination,
        model: TABLES.SUPPLIER_VEHICLE,
        userId: supplierId
    });

    return (
        <CustomModal
            {...props}
            title={t(title)}
            size={'lg'}
            onConfirm={handleSubmit}
            onHide={() => {
                onHide();
            }}
            cancelButton={!details}
            confirmButton={!details}
            classNameBody="pt-2"
        >
            {renderExpandedRow &&
                <Box sx={CustomExpandableTableStyles.expandedBox}>{renderExpandedRow(row,true,details)}</Box>
            }
            {isDecline &&
                <div className={"mt-3"}>
                <CustomTextField
                    length={512}
                    id='txt_rcm_comment'
                    name='comment'
                    value={comment}
                    trans='comment'
                    emptyFields={emptyFields}
                    required={true}
                    onChange={handleChangeComment}
                    multiline={true}
                    row={4}
                />
            </div>
            }
            { assign &&
                <div className={"row mt-5"}>
                <div className='col-12'>
                    <CustomAddChipSearch
                        autocompleteId={'slt_rcm_employeesIds'}
                        buttonId={'btn_rcm_add_employees'}
                        selectedList={selectedListEmployee}
                        setSelectedList={setSelectedListEmployee}
                        bodyApi={bodyAutocompleteSearchEmployee}
                        api={SERVICE_ADVANCED_SEARCH_SIMPLE_SEARCH_USER}
                        disabled={false}
                        label="employee"
                        emptyFields={emptyFields}
                        required={true}
                        reset={false}
                        hideButton={false}
                        keyId={"userId"}
                        showSubSection={true}
                        sanitizeDataList={userListToLabelEmailPhoneValue}
                        Icon={PersonChipBlack}
                    />
                </div>

                <div className='col-12'>
                    <CustomAddChipSearch
                        autocompleteId={'slt_rcm_vehicleId'}
                        buttonId={'btn_rcm_add_vehicle'}
                        selectedList={selectedListVehicle}
                        setSelectedList={setSelectedListVehicle}
                        api={SERVICE_ADVANCED_SEARCH_SIMPLE_SEARCH_USER}
                        bodyApi={bodyAutocompleteSearchVehicle}
                        disabled={false}
                        label="vehicle"
                        emptyFields={emptyFields}
                        required={true}
                        reset={false}
                        hideButton={false}
                        keyId={"userId"}
                        showSubSection={true}
                        sanitizeDataList={vehicleDataTransform}
                        Icon={PersonChipBlack}
                    />
                </div>
            </div>
            }
        </CustomModal>
    );
};

export default RequestCommentModal;
